import { request } from '../utils/apiHelper';
import { AppConfig } from '../types/app';
import { RememberLoginEntry } from '../utils/rememberLoginStorage';
import { v4 as uuidv4 } from 'uuid';

/** Default path if the server config does not carry rememberLoginApiPath. */
const DEFAULT_REMEMBER_LOGIN_PATH = '/api/rememberLogin';

export type RememberLoginResult =
  | { action: 'redirect'; idp: string }
  | { action: 'choose'; email: string }
  // clear: the edge found the stored entry unusable, so the page should drop it.
  | { action: 'none'; clear: boolean };

const NONE: RememberLoginResult = { action: 'none', clear: false };

const postRememberLogin = (
  config: AppConfig,
  body: Record<string, unknown>,
  metricNs: string
) =>
  request(
    config.rememberLoginApiPath || DEFAULT_REMEMBER_LOGIN_PATH,
    {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Correlation-ID': uuidv4(),
      },
      body: JSON.stringify(body),
    },
    { metricNs, logWithRum: true }
  );

// Asks /api/rememberLogin whether to redirect to the last-used IdP (within 24h)
// or show the email chooser (the next 24h). The edge identifies the user from the
// session's refresh-token cookie. Any failure resolves to a plain `none` so
// sign-in is never blocked.
export async function checkRememberLogin(
  config: AppConfig,
  clientId: string,
  entry: RememberLoginEntry
): Promise<RememberLoginResult> {
  try {
    const response = await postRememberLogin(
      config,
      { clientId, entry },
      'RememberLogin'
    );
    const data = await response.json();
    if (
      data?.action === 'redirect' &&
      typeof data.idp === 'string' &&
      data.idp
    ) {
      return { action: 'redirect', idp: data.idp };
    }
    if (
      data?.action === 'choose' &&
      typeof data.email === 'string' &&
      data.email
    ) {
      return { action: 'choose', email: data.email };
    }
    return { action: 'none', clear: data?.clear === true };
  } catch {
    // Never block sign-in on a remember-login failure.
    return NONE;
  }
}

export default checkRememberLogin;
